import React from 'react';
import {
  FieldErrors,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {Box} from 'native-base';
import {findNodeHandle} from 'react-native';
import {AgropecuaryFields} from './fields/AgropecuaryFields';
import {TextInput as Input} from '../../../../../../../components/inputs/text';
import {
  DropDownInput,
  MaskedInput,
  RifInput,
  TextArea,
} from '../../../../../../../components/inputs';

import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../../../components/buttons';
import useToast from '../../../../../../../hooks';
import {
  IAgropecuaryClient,
  IBankClient,
  ISalesmanClient,
} from '../../../../../../../interfaces/dtos';
import {IListOption} from '../../../../../../../context/user/Types';
import IDropDownOption from '../../../../../../../interfaces/ui/dropdown-option.interface';

interface FormProps {
  onSubmit?: (data: ISalesmanClient) => Promise<boolean>;
  onEndSubmit?: (success: boolean) => void;
  salesTerritories: IListOption[];
  customerTypes: IListOption[];
}

const getDefaultValues = (): IAgropecuaryClient | IBankClient => {
  return {
    sunagroCode: '',
    insaiCode: '',
  };
};

export const BasicForm = ({
  onSubmit: launchSubmitEvent,
  onEndSubmit: launchEndSubmitEvent,
  salesTerritories,
  customerTypes,
}: FormProps) => {
  const {showInputsError} = useToast();
  const {
    control,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm({
    defaultValues: {
      identity: '',
      legalName: '',
      phone: '',
      email: '',
      zoneId: '',
      clientTypeId: '',
      ...getDefaultValues(),
    } as ISalesmanClient,
  });

  const handleClearForm = () => {
    reset({
      identity: '',
      legalName: '',
      phone: '',
      email: '',
      zoneId: '',
      clientTypeId: '',
      ...getDefaultValues(),
    });
  };

  const onInvalid: SubmitErrorHandler<any> = async (
    fieldErrors: FieldErrors<any>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const onSubmit: SubmitHandler<any> = async (data: any) => {
    let success = false;
    if (launchSubmitEvent) {
      success = await launchSubmitEvent(data);
    }
    if (launchEndSubmitEvent) {
      launchEndSubmitEvent(success);
    }
  };

  const parseIListOption = (list: IListOption[]): IDropDownOption[] => {
    return list.map(item => {
      return {
        label: item.title,
        value: item.value,
      };
    });
  };

  return (
    <>
      <RifInput
        control={control}
        name="identity"
        label="salesman-add-client-identity"
        error={errors.identity}
        rules={{
          required: 'form-is-required',
        }}
        defaultValue={'J'}
      />
      <Input
        control={control}
        name="legalName"
        label="salesman-add-client-legal-name"
        error={errors.legalName}
        rules={{
          required: 'form-is-required',
        }}
      />

      <TextArea
        control={control}
        name="address"
        label="salesman-add-client-address"
        error={errors.address}
        numberOfLines={4}
        maxLength={250}
        rules={{
          required: 'form-is-required',
        }}
        onContentSizeChange={event => {
          var that: any = this;
          if (that && that.scrollRef && that.scrollRef.props) {
            that.scrollRef.props.scrollToFocusedInput(
              findNodeHandle(event.target),
            );
          }
        }}
      />
      <MaskedInput
        control={control}
        name="phone"
        label="salesman-add-client-phone"
        mask="phone"
        error={errors.phone}
        rules={{
          required: 'form-is-required',
          pattern: {
            value: /^(04|02)([0-9]{2})-[0-9]{7}$/i,
            message: 'invalid-phone-number',
          },
        }}
      />
      <Input
        control={control}
        name="email"
        label="salesman-add-client-email"
        error={errors.email}
        rules={{
          required: 'form-is-required',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'invalid-email-address',
          },
        }}
        type="email"
      />
      <AgropecuaryFields control={control} errors={errors} />
      <DropDownInput
        control={control}
        name="zoneId"
        label="salesman-add-client-salesman-zone"
        options={parseIListOption(salesTerritories)}
        error={errors.zoneId}
        rules={{
          required: 'form-is-required',
        }}
      />
      <DropDownInput
        control={control}
        name="clientTypeId"
        label="salesman-add-client-client-type"
        options={parseIListOption(customerTypes)}
        error={errors.clientTypeId}
        rules={{
          required: 'form-is-required',
        }}
      />
      <Input
        control={control}
        name="salesman"
        label="salesman-add-client-salesman-name"
        error={errors.salesman}
        defaultValue="test salesman"
        disabled={true}
      />
      <Box mt={5}>
        <PrimaryButton
          label="salesman-customer-add-continue"
          onPress={handleSubmit(onSubmit, onInvalid)}
        />
      </Box>
      <Box mt={5}>
        <SecondaryButton
          label="salesman-button-reset"
          onPress={handleClearForm}
        />
      </Box>
    </>
  );
};
